import { IHttpAdapter, IHttpGetRequestConfig } from '../../entity/interface/iHttpAdapter';
import { AxiosCacheGetConfigs } from '@intigral/constant';
import { API_IDS } from '@intigral/constant';

import { IGetRewindParams } from '../../entity/interface/iPopcorn';
import { IPopcornRepository } from '../../core/repositories/interfaces/iPopcornRepository';
import { container } from '../../DataDomain';
import { IApiPathManager } from '../config/ApiPathManager';
import { DI } from '@intigral/constant/config/DIContainer';
import { IGenreRatingConfig } from '../../entity/interface/iPopcornResponse';

class PopcornRepository implements IPopcornRepository {
    // Lazy getters for dependencies to avoid circular dependencies
    private get http(): IHttpAdapter {
        return container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
    }

    private get apiPathManager(): IApiPathManager {
        return container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    forgotPin(apikey: string, username: string, identityToken: string, authToken: string): Promise<any> {
        return this.http.get<any>(this.apiPathManager.getPath(API_IDS.POPCORN_API_FORGOT_PIN), {
            headers: {
                identityToken,
                authorization: authToken,
            },
            params: {
                apikey,
                userName: username,
                username,
                token: authToken,
            },
        });
    }

    /**
     * Gets promotions from the Popcorn API.
     *
     * @param {string} apikey - The API key to authenticate with.
     * @param {string} device - The device identifier.
     * @param {string} country - The country code.
     * @param {string} subscriptionGuids - The subscription GUIDs.
     * @param {string} tierGuid - The tier GUID.
     * @param {string} operator - The operator identifier.
     * @param {string} productKey - The product key.
     * @param {string} range - The date range filter.
     * @param {string} area - The area filter.
     * @param {string} dataSources - The area filter.
     * @returns {Promise<any>} A promise resolving to the promotions response.
     */
    getPromotions(
        apikey: string,
        device: string,
        country: string,
        subscriptionGuids: string,
        tierGuid: string,
        operator: string,
        subStatus: string,
        productKey: string,
        range: string,
        providers: { [key: string]: { [key: string]: string } },
        lang: string,
        genreRatingConfig: IGenreRatingConfig,
        dataSources: string
    ): Promise<any> {
        const path = this.apiPathManager.getByDatasource(dataSources);

        return this.http.get<IHttpGetRequestConfig>(path, {
            headers: {},
            params: {
                apikey,
                device,
                country,
                bySubscriptionGuidsPipe: subscriptionGuids,
                tierGuid,
                operator,
                subStatus,
                productKey,
                range,
            },
            requestCache: AxiosCacheGetConfigs,
        });
    }

    async getRewind(searchParams: IGetRewindParams): Promise<any> {
        return this.http.get<IHttpGetRequestConfig>(this.apiPathManager.getPath(API_IDS.POPCORN_API_GET_ALL_REWIND), {
            params: {
                ...Object.entries(searchParams).reduce(
                    (acc, [key, value]) => ({
                        ...acc,
                        [key === 'apiKey' ? 'apikey' : key]: value,
                    }),
                    {}
                ),
            },
        });
    }

    async getByPopcornUrl(url: string): Promise<any> {
        return this.http.get<IHttpGetRequestConfig>(url);
    }
}

export default PopcornRepository;
